import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../context/AuthContext'
import { useUser } from '../context/UserContext'
import { supabase, executeWithAuthRetry } from '../lib/supabase'
import { 
  Calendar, 
  Clock, 
  Plus, 
  Bell, 
  CheckCircle, 
  AlertTriangle, 
  Shield, 
  FileText, 
  Users, 
  Database, 
  Wifi, 
  Lock,
  Edit3,
  Trash2,
  Filter,
  Search,
  Download,
  RefreshCw,
  Target,
  Award,
  BookOpen,
  Settings,
  Eye,
  X,
  BarChart3,
  Loader,
  ChevronLeft,
  ChevronRight,
  Activity
} from 'lucide-react'

// Helper: sanitize display titles by removing internal tags like [AID:...] / [AG:...] / [Compliance]
const sanitizeDisplayTitle = (t) => String(t || '')
  .replace(/\[AG:[^\]]+\]\s*/gi, '')
  .replace(/\[AID:[^\]]+\]\s*/gi, '')
  .replace(/\[Compliance\]\s*/gi, '')
  .replace(/\s+/g, ' ')
  .trim()

const TaskReminders = () => {
  const { user, profile } = useAuth()
  const { canCreate, canEdit, canDelete } = useUser()
  const [tasks, setTasks] = useState([])
  const [adminRisks, setAdminRisks] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedTask, setSelectedTask] = useState(null)
  const [editingTask, setEditingTask] = useState(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterPriority, setFilterPriority] = useState('all')
  const [filterCategory, setFilterCategory] = useState('all')
  const [selectedAgency, setSelectedAgency] = useState('all')
  const [expandedAgencies, setExpandedAgencies] = useState({})
  const [showAgencyAccordion, setShowAgencyAccordion] = useState(false)
  const [currentUserAgency, setCurrentUserAgency] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [currentView, setCurrentView] = useState('list') // list, calendar, dashboard
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState(null)
  const [recentActivities, setRecentActivities] = useState([])
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    priority: '',
    due_date: '',
    reminder_date: '',
    frequency: 'once',
    assigned_to: '',
    estimated_hours: '',
    compliance_type: 'cybersecurity',
    tags: ''
  })
  const [formErrors, setFormErrors] = useState({})
  const [users, setUsers] = useState([])
  const [loadingUsers, setLoadingUsers] = useState(false)

  // Sample compliance tasks
  const defaultTasks = [
    {
      id: 1,
      title: 'Quarterly Security Training',
      description: 'Conduct mandatory cybersecurity awareness training for all staff members',
      category: 'training',
      priority: 'high',
      status: 'pending',
      dueDate: '2024-02-15',
      reminderDate: '2024-02-10',
      frequency: 'quarterly',
      assignedTo: 'HR Department',
      complianceType: 'cybersecurity',
      estimatedHours: 4,
      completionRate: 0,
      lastCompleted: null,
      nextDue: '2024-02-15',
      tags: ['training', 'mandatory', 'cybersecurity'],
      checklist: [
        'Schedule training sessions',
        'Prepare training materials',
        'Send invitations to staff',
        'Conduct training',
        'Collect feedback',
        'Generate completion report'
      ]
    },
    {
      id: 2,
      title: 'Data Privacy Impact Assessment',
      description: 'Review and update DPIA for new data processing activities',
      category: 'data-privacy',
      priority: 'high',
      status: 'in_progress',
      dueDate: '2024-02-20',
      reminderDate: '2024-02-15',
      frequency: 'annually',
      assignedTo: 'Legal Department',
      complianceType: 'data-privacy',
      estimatedHours: 8,
      completionRate: 45,
      lastCompleted: '2023-02-20',
      nextDue: '2024-02-20',
      tags: ['DPIA', 'data-privacy', 'legal'],
      checklist: [
        'Identify new data processing activities',
        'Assess privacy risks',
        'Document mitigation measures',
        'Review with stakeholders',
        'Submit for approval',
        'Implement recommendations'
      ]
    },
    {
      id: 3,
      title: 'Password Policy Review',
      description: 'Annual review and update of organizational password policies',
      category: 'access-control',
      priority: 'medium',
      status: 'pending',
      dueDate: '2024-02-25',
      reminderDate: '2024-02-20',
      frequency: 'annually',
      assignedTo: 'IT Security Team',
      complianceType: 'cybersecurity',
      estimatedHours: 6,
      completionRate: 0,
      lastCompleted: '2023-02-25',
      nextDue: '2024-02-25',
      tags: ['password', 'policy', 'security'],
      checklist: [
        'Review current password policy',
        'Analyze security incidents',
        'Update policy requirements',
        'Get management approval',
        'Communicate changes',
        'Update systems'
      ]
    },
    {
      id: 4,
      title: 'Backup System Verification',
      description: 'Test and verify integrity of backup systems and recovery procedures',
      category: 'data-protection',
      priority: 'high',
      status: 'pending',
      dueDate: '2024-03-01',
      reminderDate: '2024-02-25',
      frequency: 'monthly',
      assignedTo: 'IT Operations',
      complianceType: 'cybersecurity',
      estimatedHours: 4,
      completionRate: 0,
      lastCompleted: '2024-01-01',
      nextDue: '2024-03-01',
      tags: ['backup', 'recovery', 'testing'],
      checklist: [
        'Test backup systems',
        'Verify data integrity',
        'Test recovery procedures',
        'Document results',
        'Update procedures if needed',
        'Schedule next test'
      ]
    },
    {
      id: 5,
      title: 'Network Security Audit',
      description: 'Comprehensive audit of network security controls and configurations',
      category: 'network-security',
      priority: 'medium',
      status: 'scheduled',
      dueDate: '2024-03-05',
      reminderDate: '2024-02-28',
      frequency: 'semi-annually',
      assignedTo: 'External Auditor',
      complianceType: 'cybersecurity',
      estimatedHours: 16,
      completionRate: 0,
      lastCompleted: '2023-09-05',
      nextDue: '2024-03-05',
      tags: ['audit', 'network', 'security'],
      checklist: [
        'Schedule audit with vendor',
        'Prepare network documentation',
        'Coordinate access permissions',
        'Conduct audit',
        'Review findings',
        'Implement recommendations'
      ]
    },
    {
      id: 6,
      title: 'Incident Response Plan Update',
      description: 'Annual review and update of cybersecurity incident response procedures',
      category: 'incident-response',
      priority: 'high',
      status: 'overdue',
      dueDate: '2024-01-31',
      reminderDate: '2024-01-25',
      frequency: 'annually',
      assignedTo: 'Security Team',
      complianceType: 'cybersecurity',
      estimatedHours: 12,
      completionRate: 0,
      lastCompleted: '2023-01-31',
      nextDue: '2024-01-31',
      tags: ['incident-response', 'procedures', 'update'],
      checklist: [
        'Review current procedures',
        'Analyze recent incidents',
        'Update contact information',
        'Revise escalation procedures',
        'Test communication channels',
        'Train response team'
      ]
    }
  ]

  // Helper functions for localStorage persistence of estimated hours
  const saveEstimatedHours = (taskId, hours) => {
    try {
      const stored = JSON.parse(localStorage.getItem('taskEstimatedHours') || '{}')
      stored[taskId] = hours
      localStorage.setItem('taskEstimatedHours', JSON.stringify(stored))
    } catch (error) {
      console.error('Error saving estimated hours to localStorage:', error)
    }
  }

  const getEstimatedHours = (taskId) => {
    try {
      const stored = JSON.parse(localStorage.getItem('taskEstimatedHours') || '{}')
      return stored[taskId] || 0
    } catch (error) {
      console.error('Error reading estimated hours from localStorage:', error)
      return 0
    }
  }

  // Helper function to add activity
  const addActivity = (action, taskTitle = '', type = 'task') => {
    const newActivity = {
      id: Date.now(),
      action,
      taskTitle,
      type,
      timestamp: new Date().toISOString(),
      user: user?.email || 'User'
    }
    
    setRecentActivities(prev => {
      const updated = [newActivity, ...prev].slice(0, 10) // Keep only last 10 activities
      // Save to localStorage
      try {
        localStorage.setItem('recentActivities', JSON.stringify(updated))
      } catch (error) {
        console.error('Error saving activities to localStorage:', error)
      }
      return updated
    })
  }

  // Helper function to load activities from localStorage
  const loadActivities = () => {
    try {
      const saved = localStorage.getItem('recentActivities')
      if (saved) {
        setRecentActivities(JSON.parse(saved))
      }
    } catch (error) {
      console.error('Error loading activities from localStorage:', error)
    }
  }

  // Helper function to format activity timestamp
  const formatActivityTime = (timestamp) => {
    const now = new Date()
    const activityTime = new Date(timestamp)
    const diffInMinutes = Math.floor((now - activityTime) / (1000 * 60))
    
    if (diffInMinutes < 1) return 'Just now'
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`
    
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) return `${diffInHours} hours ago`
    
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) return `${diffInDays} days ago`
    
    return activityTime.toLocaleDateString()
  }

  // Load admin-assigned risks from agency_risk_assignments table
  const loadAdminRisks = async () => {
    if (!user) return
    
    try {
      // Get user's agency name (not agency_id since new users may not have agency_id yet)
      const { data: userData } = await supabase
        .from('users')
        .select('agency')
        .eq('id', user.id)
        .single()
      
      console.log('🔍 Admin Risk Loading - User Data:', userData);
      if (userData?.agency) {
        setCurrentUserAgency(userData.agency)
      }
      
      if (userData?.agency) {
        console.log(`🔍 Loading admin risks for agency: ${userData.agency}`);
        
        // Load admin-assigned risks for user's agency (match by agency_name)
        const { data, error } = await supabase
          .from('agency_risk_assignments')
          .select('*')
          .eq('agency_name', userData.agency)
          .order('created_at', { ascending: false })
        
        if (error) {
          if (error.code === 'PGRST205') {
            console.warn('agency_risk_assignments table not found for admin risks')
            setAdminRisks([])
            return
          }
          console.error('Error loading admin risks:', error)
          setAdminRisks([])
        } else {
          console.log(`🔍 Found ${data?.length || 0} admin risks for agency: ${userData.agency}`);
          
          // Convert to task format for display
          const formattedRisks = (data || []).map(risk => ({
            id: `risk_${risk.id}`,
            title: risk.risk_title,
            description: risk.risk_description,
            category: 'admin-assigned',
            agency: userData.agency || 'Unknown Agency',
            priority: risk.priority === 'critical' ? 'critical' : risk.priority === 'high' ? 'high' : risk.priority === 'medium' ? 'medium' : 'low',
            status: 'pending',
            dueDate: risk.mitigation_deadline || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now if no deadline
            reminderDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days from now
            assignedTo: 'Admin Assigned',
            assigned_by: 'admin',
            frequency: 'once',
            complianceType: 'cybersecurity',
            estimatedHours: 8,
            completionRate: 0,
            tags: ['admin-assigned', 'risk', 'compliance'],
            checklist: risk.mitigation_steps ? risk.mitigation_steps.split('\n').filter(step => step.trim()) : [],
            createdAt: risk.created_at,
            updatedAt: risk.updated_at,
            isAdminRisk: true,
            riskDetails: {
              id: risk.id,
              riskCategory: risk.risk_category,
              severity: risk.severity,
              impact: risk.impact_description,
              likelihood: risk.likelihood
            }
          }))
          setAdminRisks(formattedRisks)
        }
      } else {
        console.warn('🔍 User has no agency assigned, cannot load admin risks');
        setAdminRisks([])
      }
    } catch (error) {
      console.error('Error loading admin risks:', error)
      setAdminRisks([])
    }
  }

  // Load users from Supabase for assignment dropdown
  const loadUsers = async () => {
    try {
      setLoadingUsers(true)
      const { data, error } = await supabase
        .from('users')
        .select('id, name, email, user_type')
        .eq('user_type', 'government_employee') // Only include government employees
        .order('name', { ascending: true })
      
      if (error) {
        console.error('Error loading users:', error)
        setUsers([])
      } else {
        setUsers(data || [])
      }
    } catch (error) {
      console.error('Error loading users:', error)
      setUsers([])
    } finally {
      setLoadingUsers(false)
    }
  }

  // Load tasks from Supabase
  const loadTasks = async () => {
    if (!user) {
      // Don't show any tasks when not authenticated - security fix
      console.warn('User not authenticated - showing empty task list')
      setTasks([])
      setLoading(false)
      return
    }
    
    try {
      setLoading(true)
      // Determine if current user is admin-type for expanded visibility
      const isAdmin = ['administrator', 'it_administrator', 'security_officer', 'department_head', 'compliance_officer']
        .includes(String(profile?.user_type || '').toLowerCase())

      // Base query: assigned to or created by current user
      let query = supabase
        .from('tasks')
        .select('*')
        .or(`assigned_to.eq.${user.id},user_id.eq.${user.id}`)

      // Do not apply agency_id filter here; visibility is handled by RLS.
      // This ensures tasks assigned to or created by the user are always visible.

      // Final ordering
      query = query.order('created_at', { ascending: false })

      const { data, error } = await query
      
      if (error) {
        console.error('Error loading tasks:', error)
        // Don't show default tasks on error - only show user-specific tasks
        console.warn('Task loading failed - showing empty task list for security')
        setTasks([])
      } else if (data && data.length > 0) {
        // Convert database format to component format with proper field mapping
        const formattedTasks = data.map(task => {
          // Get assigned user name if available
          const getAssignedToName = async (userId) => {
            if (!userId) return 'Unassigned'
            try {
              const { data: userData } = await supabase
                .from('users')
                .select('name')
                .eq('id', userId)
                .single()
              return userData?.name || 'Unknown User'
            } catch {
              return 'Unknown User'
            }
          }
          
          return {
            id: task.id,
            title: task.title,
            description: task.description,
            category: task.category || 'training',
            agency: 'Unknown Agency',
            priority: task.priority === 1 ? 'low' : task.priority === 2 ? 'medium' : task.priority === 3 ? 'high' : task.priority === 4 ? 'critical' : 'medium',
            status: normalizeStatus(task.status),
            dueDate: task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : '',
            reminderDate: task.due_date ? new Date(new Date(task.due_date).getTime() - 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] : '', // 5 days before due date
            assignedTo: 'Loading...', // Will be updated async
            assigned_to: task.assigned_to, // Store the user ID for lookup
            user_id: task.user_id, // Store creator for permission checks
            agency_id: task.agency_id || null,
            assignedBy: task.assigned_by,
            frequency: 'once',
            complianceType: task.category || 'cybersecurity',
            estimatedHours: task.estimated_hours || getEstimatedHours(task.id),
            completionRate: normalizeStatus(task.status) === 'completed' ? 100 : normalizeStatus(task.status) === 'in_progress' ? 50 : 0,
            completedAt: task.completed_at,
            checklist: [], // Default empty checklist
            tags: Array.isArray(task.tags) ? task.tags : [],
            createdAt: task.created_at,
            updatedAt: task.updated_at
          }
        })
        
        // Update assigned names asynchronously
        const tasksWithNames = await Promise.all(
          formattedTasks.map(async (task) => {
            if (task.assigned_to) {
              try {
                const { data: userData } = await supabase
                  .from('users')
                  .select('name, agency')
                  .eq('id', task.assigned_to)
                  .single()
                task.assignedTo = userData?.name || 'Unknown User'
                task.agency = userData?.agency || currentUserAgency || 'Unknown Agency'
              } catch {
                task.assignedTo = 'Unknown User'
                task.agency = currentUserAgency || 'Unknown Agency'
              }
            } else {
              task.assignedTo = 'Unassigned'
              task.agency = currentUserAgency || 'Unknown Agency'
            }
            return task
          })
        )
        
        console.log(`Loaded ${tasksWithNames.length} tasks for user ${user.id} (${user.email})`)
        setTasks(tasksWithNames)
      } else {
        // No tasks found for this user - this is normal, don't show default tasks
        console.log(`No tasks found for user ${user.id} (${user.email}) - showing empty task list`)
        setTasks([])
      }
    } catch (error) {
      console.error('Error loading tasks:', error)
      // Security fix: Don't show unfiltered default tasks on error
      console.warn('Task loading error - showing empty task list for security')
      setTasks([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTasks()
    loadAdminRisks()
    loadActivities()
    loadUsers()
  }, [user])

  const categories = [
    { value: 'all', label: 'All Categories', icon: Target },
    { value: 'admin-assigned', label: 'Admin Assigned Risks', icon: Shield },
    { value: 'training', label: 'Training', icon: BookOpen },
    { value: 'data-privacy', label: 'Data Privacy', icon: Shield },
    { value: 'access-control', label: 'Access Control', icon: Lock },
    { value: 'data-protection', label: 'Data Protection', icon: Database },
    { value: 'network-security', label: 'Network Security', icon: Wifi },
    { value: 'incident-response', label: 'Incident Response', icon: AlertTriangle },
    { value: 'backup-recovery', label: 'Backup & Recovery', icon: Database }
  ]

  const priorities = [
    { value: 'all', label: 'All Priorities' },
    { value: 'critical', label: 'Critical Priority' },
    { value: 'high', label: 'High Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'low', label: 'Low Priority' }
  ]

  const statuses = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'completed', label: 'Completed' },
    { value: 'overdue', label: 'Overdue' }
  ]

  // Normalize status values coming from various sources to match Tasks table enum
  const normalizeStatus = (raw) => {
    if (!raw) return 'pending'
    const s = String(raw).trim().toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_')
    if (['in_progress', 'inprogress', 'progress'].includes(s)) return 'in_progress'
    if (['complete', 'completed', 'done', 'finished'].includes(s)) return 'completed'
    if (['overdue', 'late', 'past_due', 'pastdue'].includes(s)) return 'overdue'
    if (['pending', 'todo', 'to_do', 'not_started', 'new'].includes(s)) return 'pending'
    return s
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-200 text-red-900 border-red-300'
      case 'high': return 'bg-red-100 text-red-800 border-red-200'
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'low': return 'bg-green-100 text-green-800 border-green-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getStatusColor = (status) => {
    const s = normalizeStatus(status)
    switch (s) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200'
      case 'in_progress': return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'scheduled': return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'overdue': return 'bg-red-100 text-red-800 border-red-200'
      case 'pending': return 'bg-gray-100 text-gray-800 border-gray-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  // Format status for display (e.g., 'in_progress' -> 'In Progress', 'pending' -> 'Pending')
  const formatStatusLabel = (status) => {
    const s = normalizeStatus(status)
    if (s === 'in_progress') return 'In Progress'
    if (s === 'completed') return 'Completed'
    if (s === 'overdue') return 'Overdue'
    return 'Pending'
  }

  const getCategoryIcon = (category) => {
    const categoryObj = categories.find(cat => cat.value === category)
    return categoryObj ? categoryObj.icon : Target
  }

  const filteredTasks = [...tasks, ...adminRisks].filter(task => {
    const matchesStatus = filterStatus === 'all' || task.status === filterStatus
    const matchesPriority = filterPriority === 'all' || task.priority === filterPriority
    const matchesCategory = filterCategory === 'all' || task.category === filterCategory
    const matchesAgency = selectedAgency === 'all' || (task.agency || 'Unknown Agency') === selectedAgency
    const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         task.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         task.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    
    return matchesStatus && matchesPriority && matchesCategory && matchesAgency && matchesSearch
  })

  // Security logging for debugging
  useEffect(() => {
    console.log(`Task filtering debug - User: ${user?.id} (${user?.email}), Tasks: ${tasks.length}, AdminRisks: ${adminRisks.length}, Filtered: ${filteredTasks.length}`)
    console.log(`Task filtering debug - Status: ${filterStatus}, Priority: ${filterPriority}, Category: ${filterCategory}, Search: "${searchTerm}"`)
  }, [tasks, adminRisks, filteredTasks, user, filterStatus, filterPriority, filterCategory, searchTerm])

  const getTaskStats = (tasksToAnalyze) => {
    const total = tasksToAnalyze.length
    const completed = tasksToAnalyze.filter(task => task.status === 'completed').length
    const overdue = tasksToAnalyze.filter(task => task.status === 'overdue').length
    const pending = tasksToAnalyze.filter(task => task.status === 'pending').length
    const inProgress = tasksToAnalyze.filter(task => task.status === 'in_progress').length
    const adminRisksCount = tasksToAnalyze.filter(task => adminRisks.includes(task)).length
    
    return { total, completed, overdue, pending, inProgress, adminRisksCount }
  }

  const stats = getTaskStats(filteredTasks)

  // Derive available agencies from tasks and admin risks
  const availableAgencies = Array.from(new Set([...tasks, ...adminRisks]
    .map(t => t.agency || 'Unknown Agency'))).sort()

  // Expand all agencies by default when the list changes
  useEffect(() => {
    const initialExpanded = {}
    availableAgencies.forEach(a => { initialExpanded[a] = true })
    setExpandedAgencies(initialExpanded)
  }, [availableAgencies.join('|')])

  // Calendar utility functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay()
  }

  const getTasksForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0]
    return filteredTasks.filter(task => 
      task.dueDate === dateStr || task.reminderDate === dateStr
    )
  }

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const errors = {}
    
    if (!formData.title.trim()) {
      errors.title = 'Task title is required'
    }
    
    if (!formData.description.trim()) {
      errors.description = 'Description is required'
    }
    
    if (!formData.category) {
      errors.category = 'Category is required'
    }
    
    if (!formData.priority) {
      errors.priority = 'Priority is required'
    }
    
    if (!formData.due_date) {
      errors.due_date = 'Due date is required'
    }
    
    if (!formData.assigned_to.trim()) {
      errors.assigned_to = 'Assigned to is required'
    }
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm() || !user) return
    
    try {
      setSaving(true)
      
      // Convert priority to number
      const priorityMap = { 'low': 1, 'medium': 2, 'high': 3, 'critical': 4 }
      const priorityNumber = priorityMap[formData.priority] || 2
      
      // Parse tags
      const tagsArray = formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : []
      
      const taskData = {
        user_id: user.id,
        title: formData.title,
        description: formData.description,
        category: formData.category,
        priority: priorityNumber,
        due_date: formData.due_date,
        status: editingTask ? editingTask.status : 'pending',
        assigned_by: user.id,
        assigned_to: formData.assigned_to || null,
        agency_id: profile?.agency_id || null,
        // estimated_hours: formData.estimated_hours ? parseInt(formData.estimated_hours) : null, // Temporarily disabled due to schema cache issue
        tags: tagsArray
      }
      
      let result
      if (editingTask) {
        // Update existing task
        result = await supabase
          .from('tasks')
          .update(taskData)
          .eq('id', editingTask.id)
          .select()
      } else {
        // Create new task
        result = await supabase
          .from('tasks')
          .insert([taskData])
          .select()
      }
      
      const { data, error } = result
      
      if (error) {
        console.error(`Error ${editingTask ? 'updating' : 'creating'} task:`, error)
        const action = editingTask ? 'update' : 'create'
        console.error(`Error ${action}ing task:`, error)
        alert(`Failed to ${action} task: ${error.message || 'Unknown error'}. Please try again.`)
      } else {
        // Update local state with estimated hours since it's not saved to database
        if (data && data[0]) {
          const estimatedHours = formData.estimated_hours ? parseInt(formData.estimated_hours) : 0
          
          // Save estimated hours to localStorage for persistence across refreshes
          if (estimatedHours > 0) {
            saveEstimatedHours(data[0].id, estimatedHours)
          }
          
          const updatedTask = {
            ...data[0],
            estimatedHours: estimatedHours
          }
          
          if (editingTask) {
            // Update existing task in local state
            setTasks(prevTasks => 
              prevTasks.map(task => 
                task.id === editingTask.id 
                  ? { ...task, ...updatedTask, estimatedHours: estimatedHours }
                  : task
              )
            )
            // Add activity for task update
            addActivity(`Updated task: ${data[0].title}`, data[0].title, 'update')
            // Dispatch custom event to notify other components
            window.dispatchEvent(new CustomEvent('taskUpdated'))
          } else {
            // Add new task to local state
            const newTask = {
              id: data[0].id,
              title: data[0].title,
              description: data[0].description,
              category: data[0].category || 'training',
              priority: data[0].priority === 1 ? 'low' : data[0].priority === 2 ? 'medium' : data[0].priority === 3 ? 'high' : data[0].priority === 4 ? 'critical' : 'medium',
              status: normalizeStatus(data[0].status),
              dueDate: data[0].due_date ? new Date(data[0].due_date).toISOString().split('T')[0] : '',
              reminderDate: data[0].due_date ? new Date(new Date(data[0].due_date).getTime() - 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] : '',
              assignedTo: 'You',
              assignedBy: data[0].assigned_by,
              frequency: 'once',
              complianceType: data[0].category || 'cybersecurity',
              estimatedHours: estimatedHours,
              completionRate: 0,
              completedAt: data[0].completed_at,
              checklist: [],
              tags: Array.isArray(data[0].tags) ? data[0].tags : [],
              createdAt: data[0].created_at,
              updatedAt: data[0].updated_at
            }
            setTasks(prevTasks => [newTask, ...prevTasks])
            // Add activity for task creation
            addActivity(`Created new task: ${data[0].title}`, data[0].title, 'create')
            // Dispatch custom event to notify other components
            window.dispatchEvent(new CustomEvent('taskUpdated'))
          }
        }
        
        // Reset form
        resetForm()
        setShowCreateModal(false)
        setShowEditModal(false)
      }
    } catch (error) {
        const action = editingTask ? 'update' : 'create'
        console.error(`Error ${action}ing task:`, error)
        alert(`Failed to ${action} task: ${error.message || 'Unknown error'}. Please try again.`)
    } finally {
      setSaving(false)
    }
  }

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      priority: '',
      due_date: '',
      reminder_date: '',
      frequency: 'once',
      assigned_to: '',
      estimated_hours: '',
      compliance_type: 'cybersecurity',
      tags: ''
    })
    setFormErrors({})
    setEditingTask(null)
  }

  const handleCreateTask = () => {
    resetForm()
    setShowCreateModal(true)
  }

  const handleEditTask = (task) => {
    // Convert priority number back to string
    const priorityMap = { 1: 'low', 2: 'medium', 3: 'high', 4: 'critical' }
    const priorityString = priorityMap[task.priority] || 'medium'
    
    // Convert tags array back to string
    const tagsString = Array.isArray(task.tags) ? task.tags.join(', ') : ''
    
    setEditingTask(task)
    setFormData({
      title: task.title || '',
      description: task.description || '',
      category: task.category || '',
      priority: priorityString,
      due_date: task.dueDate || '',
      reminder_date: task.reminderDate || '',
      frequency: task.frequency || 'once',
      assigned_to: task.assigned_to || '',
      estimated_hours: task.estimatedHours?.toString() || '',
      compliance_type: task.complianceType || 'cybersecurity',
      tags: tagsString
    })
    setFormErrors({})
    setSelectedTask(null)
    setShowEditModal(true)
  }

  const handleAcknowledgeRisk = async (risk) => {
    try {
      // Update the risk assignment to mark it as acknowledged
      const { error } = await supabase
        .from('agency_risk_assignments')
        .update({ 
          status: 'acknowledged',
          acknowledged_at: new Date().toISOString()
        })
        .eq('id', risk.id)

      if (error) throw error

      // Update local state
      setAdminRisks(prev => prev.filter(r => r.id !== `risk_${risk.id}`))
      setSelectedTask(null)
      
      alert('Risk acknowledged successfully!')
    } catch (error) {
      console.error('Error acknowledging risk:', error)
      alert('Failed to acknowledge risk. Please try again.')
    }
  }

  // Add delete task function
  const handleDeleteTask = async (taskId) => {
    console.log('Delete button clicked for task ID:', taskId)
    console.log('Current tasks:', tasks)
    console.log('Current user:', user)
    
    if (!taskId) {
      console.log('No task ID provided')
      return
    }
    
    if (!confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      console.log('User cancelled deletion')
      return
    }
    
    try {
      setSaving(true)
      console.log('Starting deletion process...')
      
      // Detect admin-assigned risk items (IDs like "risk_123")
      const isAdminRiskId = typeof taskId === 'string' && taskId.startsWith('risk_')
      if (isAdminRiskId) {
        const riskNumericId = parseInt(taskId.replace('risk_', ''), 10)
        if (Number.isNaN(riskNumericId)) {
          alert('Invalid admin risk ID. Please try again.')
          setSaving(false)
          return
        }
        console.log('Detected admin risk, numeric ID:', riskNumericId)

        // Mark the admin risk as acknowledged and remove from local state
        const { error: riskError } = await supabase
          .from('agency_risk_assignments')
          .update({ 
            status: 'acknowledged',
            acknowledged_at: new Date().toISOString()
          })
          .eq('id', riskNumericId)

        if (riskError) {
          console.error('Error acknowledging admin risk:', riskError)
          alert(`Failed to remove admin risk: ${riskError.message || 'Unknown error'}.`)
          setSaving(false)
          return
        }

        // Remove from adminRisks state
        setAdminRisks(prev => prev.filter(r => r.id !== `risk_${riskNumericId}`))
        setSelectedTask(null)
        window.dispatchEvent(new CustomEvent('taskUpdated'))
        alert('Admin-assigned risk removed from your list.')
        setSaving(false)
        return
      }

      // Check if this is a default sample task (ID 1-4) or a real database task
      const task = tasks.find(t => t.id === taskId)
      console.log('Found task:', task)
      const isDefaultTask = task && typeof task.id === 'number' && task.id <= 4
      console.log('Is default task:', isDefaultTask)
      
      if (!isDefaultTask && user) {
        console.log('Attempting to delete from database...')
        // Only try to delete from database if it's not a default task and user is authenticated
        const { error } = await supabase
          .from('tasks')
          .delete()
          .eq('id', taskId)
        
        if (error) {
          console.error('Database deletion error:', error)
          alert(`Failed to delete task: ${error.message || 'Unknown error'}. Please try again.`)
          return
        }
        console.log('Successfully deleted from database')
      }

      // Add activity for task deletion
      if (task) {
        addActivity(`Deleted task: ${task.title}`, task.title, 'delete')
        console.log('Added deletion activity')
      }
      
      // Remove task from local state (works for both default and database tasks)
      console.log('Removing task from local state...')
      setTasks(prev => {
        const newTasks = prev.filter(task => task.id !== taskId)
        console.log('Tasks before deletion:', prev.length)
        console.log('Tasks after deletion:', newTasks.length)
        return newTasks
      })
      setSelectedTask(null)
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('taskUpdated'))
      console.log('Dispatched taskUpdated event')
      
      alert('Task deleted successfully!')
      
    } catch (error) {
      console.error('Error deleting task:', error)
      alert('Failed to delete task. Please try again.')
    } finally {
      setSaving(false)
      console.log('Delete operation completed')
    }
  }

  // Add update task status function
  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    if (!user || !taskId) return
    
    try {
      setSaving(true)
      // Find the task early to determine compliance and existing assessment link
      const task = tasks.find(t => t.id === taskId)
      const isCompliance = task && typeof task.title === 'string' && task.title.toLowerCase().includes('[compliance]')
      
      const updateData = {
        status: newStatus,
        updated_at: new Date().toISOString()
      }
      
      // If marking as completed, set completed_at timestamp and backfill assessment_id when appropriate
      if (newStatus === 'completed') {
        updateData.completed_at = new Date().toISOString()
        if (isCompliance) {
          try {
            const runLatest = async () => supabase
              .from('assessments')
              .select('*')
              .eq('user_id', user.id)
              .eq('status', 'completed')
              .order('completed_at', { ascending: false })
              .limit(1)
              .single()
            const { data: latestAssessment } = await executeWithAuthRetry(runLatest)
            if (latestAssessment && latestAssessment.id) {
              updateData.assessment_id = latestAssessment.id
            }
          } catch (e) {
            console.warn('Failed to fetch latest assessment for backfill during completion:', e)
          }
        }
      } else {
        // Clear completed_at when changing away from completed
        updateData.completed_at = null
      }
      
      const { error } = await supabase
        .from('tasks')
        .update(updateData)
        .eq('id', taskId)
      
      if (error) {
        console.error('Error updating task status:', error)
        alert(`Failed to update task status: ${error.message || 'Unknown error'}. Please try again.`)
      } else {
        // Update local state
        setTasks(prev => prev.map(t => 
          t.id === taskId 
            ? { 
                ...t, 
                status: normalizeStatus(newStatus), 
                completionRate: normalizeStatus(newStatus) === 'completed' ? 100 : normalizeStatus(newStatus) === 'in_progress' ? 50 : 0,
                completedAt: normalizeStatus(newStatus) === 'completed' ? new Date().toISOString() : null,
                // Reflect assessment_id in UI state if we backfilled it
                assessment_id: (normalizeStatus(newStatus) === 'completed' && updateData.assessment_id) ? updateData.assessment_id : t.assessment_id
              }
            : t
        ))
        
        // Add activity for status change
        if (task) {
          const statusDisplay = normalizeStatus(newStatus).replace('_', ' ')
          addActivity(`Updated task status to ${statusDisplay}: ${task.title}`, task.title, 'status_update')
          // Dispatch custom event to notify other components
          window.dispatchEvent(new CustomEvent('taskUpdated'))
        }
        
        // Show success message
        const statusDisplay = normalizeStatus(newStatus).replace('_', ' ')
        console.log(`Task status updated to: ${statusDisplay}`)
        // Optional: You can add a toast notification here instead of alert
      }
    } catch (error) {
      console.error('Error updating task status:', error)
      alert('Failed to update task status. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate)
    newDate.setMonth(currentDate.getMonth() + direction)
    setCurrentDate(newDate)
  }

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gov-50 to-cyber-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gov-800 mb-2">Task Reminders</h1>
              <p className="text-gov-600">Manage compliance tasks and cybersecurity reminders for your organization</p>
            </div>
            <div className="flex items-center space-x-4 mt-4 lg:mt-0">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentView('list')}
                  className={`px-3 py-2 rounded-lg transition-colors ${
                    currentView === 'list' ? 'bg-cyber-600 text-white' : 'bg-white text-gov-600 hover:bg-gov-50'
                  }`}
                >
                  List
                </button>
                <button
                  onClick={() => setCurrentView('calendar')}
                  className={`px-3 py-2 rounded-lg transition-colors ${
                    currentView === 'calendar' ? 'bg-cyber-600 text-white' : 'bg-white text-gov-600 hover:bg-gov-50'
                  }`}
                >
                  Calendar
                </button>
                <button
                  onClick={() => setCurrentView('dashboard')}
                  className={`px-3 py-2 rounded-lg transition-colors ${
                    currentView === 'dashboard' ? 'bg-cyber-600 text-white' : 'bg-white text-gov-600 hover:bg-gov-50'
                  }`}
                >
                  Dashboard
                </button>
              </div>
              {canCreate() && (
                <button
                  onClick={handleCreateTask}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create Task</span>
                </button>
              )}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
            <motion.div variants={itemVariants} className="card text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mx-auto mb-2">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <div className="text-2xl font-bold text-gov-800">{stats.total}</div>
              <div className="text-sm text-gov-600">Total Tasks</div>
            </motion.div>
            <motion.div variants={itemVariants} className="card text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mx-auto mb-2">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="text-2xl font-bold text-gov-800">{stats.completed}</div>
              <div className="text-sm text-gov-600">Completed</div>
            </motion.div>
            <motion.div variants={itemVariants} className="card text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-lg mx-auto mb-2">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="text-2xl font-bold text-gov-800">{stats.overdue}</div>
              <div className="text-sm text-gov-600">Overdue</div>
            </motion.div>
            <motion.div variants={itemVariants} className="card text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-lg mx-auto mb-2">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="text-2xl font-bold text-gov-800">{stats.pending}</div>
              <div className="text-sm text-gov-600">Pending</div>
            </motion.div>
            <motion.div variants={itemVariants} className="card text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg mx-auto mb-2">
                <RefreshCw className="w-6 h-6 text-purple-600" />
              </div>
              <div className="text-2xl font-bold text-gov-800">{stats.inProgress}</div>
              <div className="text-sm text-gov-600">In Progress</div>
            </motion.div>
            <motion.div variants={itemVariants} className="card text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-orange-100 rounded-lg mx-auto mb-2">
                <Shield className="w-6 h-6 text-orange-600" />
              </div>
              <div className="text-2xl font-bold text-gov-800">{stats.adminRisksCount}</div>
              <div className="text-sm text-gov-600">Admin Risks</div>
            </motion.div>
          </div>
        </motion.div>

        {/* Agency Filter Button + Accordion Panel */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4"
        >
          <div className="flex items-center justify-start">
            <button
              className="btn-secondary flex items-center space-x-2"
              onClick={() => setShowAgencyAccordion((prev) => !prev)}
            >
              <Filter className="w-4 h-4" />
              <span>{selectedAgency === 'all' ? 'All Agencies' : selectedAgency}</span>
            </button>
          </div>
          {showAgencyAccordion && (
            <div className="card mt-3">
              <div className="space-y-1">
                <div
                  className="flex items-center justify-between px-3 py-2 rounded hover:bg-gov-50 cursor-pointer"
                  onClick={() => { setSelectedAgency('all'); setShowAgencyAccordion(false) }}
                >
                  <span className="font-medium">All Agencies</span>
                  <span className="text-sm text-gov-500">{[...tasks, ...adminRisks].length} tasks</span>
                </div>
                {availableAgencies.map((agencyName) => (
                  <div
                    key={agencyName}
                    className="flex items-center justify-between px-3 py-2 rounded hover:bg-gov-50 cursor-pointer"
                    onClick={() => { setSelectedAgency(agencyName); setShowAgencyAccordion(false) }}
                  >
                    <span className={`font-medium ${selectedAgency === agencyName ? 'text-cyber-600' : 'text-gov-800'}`}>{agencyName}</span>
                    <span className="text-sm text-gov-500">{[...tasks, ...adminRisks].filter(t => (t.agency || 'Unknown Agency') === agencyName).length} tasks</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card mb-6"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gov-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search tasks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gov-200 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
                />
              </div>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-2 border border-gov-200 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
              >
                {categories.map(category => (
                  <option key={category.value} value={category.value}>{category.label}</option>
                ))}
              </select>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="px-3 py-2 border border-gov-200 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
              >
                {priorities.map(priority => (
                  <option key={priority.value} value={priority.value}>{priority.label}</option>
                ))}
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gov-200 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
              >
                {statuses.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <button className="btn-secondary flex items-center space-x-2">
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
              <button className="btn-secondary flex items-center space-x-2">
                <RefreshCw className="w-4 h-4" />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Task List */}
        {currentView === 'list' && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-4"
          >
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader className="w-8 h-8 animate-spin text-cyber-600" />
                <span className="ml-2 text-gov-600">Loading tasks...</span>
              </div>
            ) : filteredTasks.length === 0 ? (
              <div className="text-center py-12">
                <Target className="w-16 h-16 text-gov-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gov-600 mb-2">No tasks found</h3>
                <p className="text-gov-500">Create your first compliance task to get started.</p>
              </div>
            ) : (
              Object.entries(
                filteredTasks.reduce((acc, t) => {
                  const key = t.agency || 'Unknown Agency'
                  acc[key] = acc[key] || []
                  acc[key].push(t)
                  return acc
                }, {})
              ).map(([agencyName, agencyTasks]) => (
                <div key={agencyName} className="card">
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setExpandedAgencies(prev => ({ ...prev, [agencyName]: !prev[agencyName] }))}
                  >
                    <h3 className="text-lg font-semibold text-gov-800">{agencyName}</h3>
                    <span className="text-sm text-gov-600">{agencyTasks.length} task{agencyTasks.length !== 1 ? 's' : ''}</span>
                  </div>
                  {expandedAgencies[agencyName] && (
                    <div className="space-y-4 mt-4">
                      {agencyTasks.map((task) => {
                        const CategoryIcon = getCategoryIcon(task.category)
                        return (
                          <motion.div
                            key={task.id}
                            variants={itemVariants}
                            className="card hover:shadow-lg transition-shadow"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start space-x-4 flex-1">
                                <div className="flex items-center justify-center w-12 h-12 bg-cyber-100 rounded-lg flex-shrink-0">
                                  <CategoryIcon className="w-6 h-6 text-cyber-600" />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2 mb-2">
                                    <h3 
                                      className="text-lg font-semibold text-gov-800 cursor-pointer hover:text-cyber-600 transition-colors"
                                      onClick={() => setSelectedTask(task)}
                                    >
                                      {sanitizeDisplayTitle(task.title)}
                                    </h3>
                                    {task.isAdminRisk && (
                                      <span className="px-2 py-1 text-xs rounded-full border bg-purple-100 text-purple-800 border-purple-200 flex items-center">
                                        <Shield className="w-3 h-3 mr-1" />
                                        Admin Assigned
                                      </span>
                                    )}
                                    <span className={`px-2 py-1 text-xs rounded-full border ${getPriorityColor(task.priority)}`}>
                                      {task.priority}
                                    </span>
                                    <span className={`px-2 py-1 text-xs rounded-full border ${getStatusColor(task.status)}`}>
                                      {task.status.replace('_', ' ')}
                                    </span>
                                  </div>
                                  <p className="text-gov-600 mb-3">{task.description}</p>
                                  <div className="flex flex-wrap items-center gap-4 text-sm text-gov-500">
                                    <div className="flex items-center space-x-1">
                                      <Calendar className="w-4 h-4" />
                                      <span>Due: {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'No due date'}</span>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                      <Bell className="w-4 h-4" />
                                      <span>Reminder: {task.reminderDate ? new Date(task.reminderDate).toLocaleDateString() : 'No reminder'}</span>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                      <Users className="w-4 h-4" />
                                      <span>{task.assignedTo} • {task.agency || 'Unknown Agency'}</span>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                      <Clock className="w-4 h-4" />
                                      <span>{task.estimatedHours || 'N/A'}h estimated</span>
                                    </div>
                                  </div>
                                  {task.completionRate > 0 && (
                                    <div className="mt-3">
                                      <div className="flex items-center justify-between text-sm text-gov-600 mb-1">
                                        <span>Progress</span>
                                        <span>{task.completionRate}%</span>
                                      </div>
                                      <div className="w-full bg-gov-200 rounded-full h-2">
                                        <div 
                                          className="bg-cyber-600 h-2 rounded-full transition-all duration-300"
                                          style={{ width: `${task.completionRate}%` }}
                                        />
                                      </div>
                                    </div>
                                  )}
                                  {/* Quick Status Update */}
                                  <div className="mt-3 flex items-center space-x-2">
                                    <span className="text-sm text-gov-600">Quick update:</span>
                                    <select
                                      value={task.status}
                                      onChange={(e) => handleUpdateTaskStatus(task.id, e.target.value)}
                                      className="text-xs border border-gov-200 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-cyber-500"
                                      disabled={saving}
                                    >
                                      <option value="pending">Pending</option>
                                      <option value="in_progress">In Progress</option>
                                      <option value="completed">Completed</option>
                                      <option value="overdue">Overdue</option>
                                    </select>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                {canEdit() && (
                                  <button 
                                    onClick={() => handleEditTask(task)}
                                    className="p-2 text-gov-600 hover:text-cyber-600 hover:bg-cyber-50 rounded-lg transition-colors"
                                    title="Edit task"
                                  >
                                    <Edit3 className="w-4 h-4" />
                                  </button>
                                )}
                                {canDelete() && (task.user_id === user?.id || task.assigned_to === user?.id) && (
                                  <button 
                                    onClick={() => handleDeleteTask(task.id)}
                                    className="p-2 text-gov-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Delete task"
                                    disabled={saving}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        )
                      })}
                    </div>
                  )}
                </div>
              ))
            )}
          </motion.div>
        )}

        {/* Calendar View */}
        {currentView === 'calendar' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card"
          >
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gov-800">{formatMonthYear(currentDate)}</h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => navigateMonth(-1)}
                  className="p-2 text-gov-600 hover:text-cyber-600 hover:bg-cyber-50 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setCurrentDate(new Date())}
                  className="px-3 py-2 text-sm font-medium text-gov-600 hover:text-cyber-600 hover:bg-cyber-50 rounded-lg transition-colors"
                >
                  Today
                </button>
                <button
                  onClick={() => navigateMonth(1)}
                  className="p-2 text-gov-600 hover:text-cyber-600 hover:bg-cyber-50 rounded-lg transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {/* Day Headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="p-3 text-center text-sm font-medium text-gov-600 bg-gov-50 rounded-lg">
                  {day}
                </div>
              ))}
              
              {/* Calendar Days */}
              {(() => {
                const daysInMonth = getDaysInMonth(currentDate)
                const firstDay = getFirstDayOfMonth(currentDate)
                const days = []
                
                // Empty cells for days before the first day of the month
                for (let i = 0; i < firstDay; i++) {
                  days.push(
                    <div key={`empty-${i}`} className="p-2 h-24 border border-gov-100 rounded-lg bg-gray-50"></div>
                  )
                }
                
                // Days of the month
                for (let day = 1; day <= daysInMonth; day++) {
                  const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
                  const tasksForDay = getTasksForDate(date)
                  const isToday = date.toDateString() === new Date().toDateString()
                  const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString()
                  
                  days.push(
                    <div
                      key={day}
                      className={`p-2 h-24 border border-gov-100 rounded-lg cursor-pointer transition-colors ${
                        isToday ? 'bg-cyber-50 border-cyber-200' : 'bg-white hover:bg-gov-50'
                      } ${
                        isSelected ? 'ring-2 ring-cyber-500' : ''
                      }`}
                      onClick={() => setSelectedDate(date)}
                    >
                      <div className={`text-sm font-medium mb-1 ${
                        isToday ? 'text-cyber-600' : 'text-gov-800'
                      }`}>
                        {day}
                      </div>
                      <div className="space-y-1">
                        {tasksForDay.slice(0, 2).map(task => (
                          <div
                            key={task.id}
                            className={`text-xs px-1 py-0.5 rounded truncate ${
                              task.status === 'overdue' ? 'bg-red-100 text-red-700' :
                              task.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                              'bg-blue-100 text-blue-700'
                            }`}
                            title={sanitizeDisplayTitle(task.title)}
                          >
                            {sanitizeDisplayTitle(task.title)}
                          </div>
                        ))}
                        {tasksForDay.length > 2 && (
                          <div className="text-xs text-gov-500">
                            +{tasksForDay.length - 2} more
                          </div>
                        )}
                      </div>
                    </div>
                  )
                }
                
                return days
              })()}
            </div>

            {/* Selected Date Tasks */}
            {selectedDate && (
              <div className="mt-6 pt-6 border-t border-gov-200">
                <h3 className="text-lg font-semibold text-gov-800 mb-4">
                  Tasks for {selectedDate.toLocaleDateString()}
                </h3>
                {(() => {
                  const tasksForSelectedDate = getTasksForDate(selectedDate)
                  if (tasksForSelectedDate.length === 0) {
                    return (
                      <p className="text-gov-600 text-center py-4">No tasks scheduled for this date</p>
                    )
                  }
                  return (
                    <div className="space-y-3">
                      {tasksForSelectedDate.map(task => {
                        const CategoryIcon = getCategoryIcon(task.category)
                        return (
                          <div
                            key={task.id}
                            className="flex items-center space-x-3 p-3 bg-gov-50 rounded-lg cursor-pointer hover:bg-gov-100 transition-colors"
                            onClick={() => setSelectedTask(task)}
                          >
                            <div className="flex items-center justify-center w-8 h-8 bg-cyber-100 rounded-lg flex-shrink-0">
                              <CategoryIcon className="w-4 h-4 text-cyber-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <h4 className="font-medium text-gov-800">{sanitizeDisplayTitle(task.title)}</h4>
                                <span className={`px-2 py-0.5 text-xs rounded-full border ${getPriorityColor(task.priority)}`}>
                                  {task.priority}
                                </span>
                                <span className={`px-2 py-0.5 text-xs rounded-full border ${getStatusColor(task.status)}`}>
                                  {formatStatusLabel(task.status)}
                                </span>
                              </div>
                              <p className="text-sm text-gov-600 truncate">{task.description}</p>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )
                })()}
              </div>
            )}
          </motion.div>
        )}

        {/* Dashboard View */}
        {currentView === 'dashboard' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Dashboard Header */}
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gov-800">Analytics Dashboard</h2>
                  <p className="text-gov-600">Comprehensive overview of your compliance tasks and performance</p>
                </div>
                <div className="flex items-center space-x-2">
                  <button className="btn-secondary flex items-center space-x-2">
                    <Download className="w-4 h-4" />
                    <span>Export Report</span>
                  </button>
                  <button className="btn-secondary flex items-center space-x-2">
                    <RefreshCw className="w-4 h-4" />
                    <span>Refresh</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Key Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="card bg-gradient-to-br from-cyber-500 to-cyber-600 text-white"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-cyber-100 text-sm font-medium">Total Tasks</p>
                    <p className="text-3xl font-bold">{filteredTasks.length}</p>
                    <p className="text-cyber-200 text-sm mt-1">Active compliance items</p>
                  </div>
                  <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                    <Target className="w-8 h-8" />
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="card bg-gradient-to-br from-green-500 to-green-600 text-white"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm font-medium">Completed</p>
                    <p className="text-3xl font-bold">{filteredTasks.filter(t => t.status === 'completed').length}</p>
                    <p className="text-green-200 text-sm mt-1">
                      {filteredTasks.length > 0 ? Math.round((filteredTasks.filter(t => t.status === 'completed').length / filteredTasks.length) * 100) : 0}% completion rate
                    </p>
                  </div>
                  <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                    <CheckCircle className="w-8 h-8" />
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="card bg-gradient-to-br from-yellow-500 to-yellow-600 text-white"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-yellow-100 text-sm font-medium">Pending</p>
                    <p className="text-3xl font-bold">{filteredTasks.filter(t => t.status === 'pending').length}</p>
                    <p className="text-yellow-200 text-sm mt-1">Awaiting action</p>
                  </div>
                  <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                    <Clock className="w-8 h-8" />
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="card bg-gradient-to-br from-red-500 to-red-600 text-white"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-100 text-sm font-medium">Overdue</p>
                    <p className="text-3xl font-bold">{filteredTasks.filter(t => t.status === 'overdue' || (t.dueDate && new Date(t.dueDate) < new Date() && t.status !== 'completed')).length}</p>
                    <p className="text-red-200 text-sm mt-1">Requires attention</p>
                  </div>
                  <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                    <AlertTriangle className="w-8 h-8" />
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Charts and Analytics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Task Status Distribution */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="card"
              >
                <h3 className="text-lg font-semibold text-gov-800 mb-4">Task Status Distribution</h3>
                <div className="space-y-4">
                  {[
                    { status: 'completed', label: 'Completed', color: 'bg-green-500', count: filteredTasks.filter(t => t.status === 'completed').length },
                    { status: 'pending', label: 'Pending', color: 'bg-yellow-500', count: filteredTasks.filter(t => t.status === 'pending').length },
                    { status: 'in_progress', label: 'In Progress', color: 'bg-blue-500', count: filteredTasks.filter(t => t.status === 'in_progress').length },
                    { status: 'overdue', label: 'Overdue', color: 'bg-red-500', count: filteredTasks.filter(t => t.status === 'overdue' || (t.dueDate && new Date(t.dueDate) < new Date() && t.status !== 'completed')).length }
                  ].map((item) => {
                    const percentage = filteredTasks.length > 0 ? (item.count / filteredTasks.length) * 100 : 0
                    return (
                      <div key={item.status} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`w-4 h-4 rounded ${item.color}`}></div>
                          <span className="text-gov-700 font-medium">{item.label}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="w-32 bg-gov-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${item.color} transition-all duration-500`}
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                          <span className="text-gov-600 text-sm font-medium w-12 text-right">{item.count}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </motion.div>

              {/* Priority Distribution */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="card"
              >
                <h3 className="text-lg font-semibold text-gov-800 mb-4">Priority Distribution</h3>
                <div className="space-y-4">
                  {[
                    { priority: 'high', label: 'High Priority', color: 'bg-red-500', count: filteredTasks.filter(t => t.priority === 'high').length },
                    { priority: 'medium', label: 'Medium Priority', color: 'bg-yellow-500', count: filteredTasks.filter(t => t.priority === 'medium').length },
                    { priority: 'low', label: 'Low Priority', color: 'bg-green-500', count: filteredTasks.filter(t => t.priority === 'low').length }
                  ].map((item) => {
                    const percentage = filteredTasks.length > 0 ? (item.count / filteredTasks.length) * 100 : 0
                    return (
                      <div key={item.priority} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`w-4 h-4 rounded ${item.color}`}></div>
                          <span className="text-gov-700 font-medium">{item.label}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="w-32 bg-gov-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${item.color} transition-all duration-500`}
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                          <span className="text-gov-600 text-sm font-medium w-12 text-right">{item.count}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </motion.div>
            </div>

            {/* Category Analysis and Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Category Breakdown */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="card"
              >
                <h3 className="text-lg font-semibold text-gov-800 mb-4">Tasks by Category</h3>
                <div className="space-y-3">
                  {categories.filter(cat => cat.value !== 'all').map((category) => {
                    const count = filteredTasks.filter(t => t.category === category.value).length
                    const percentage = filteredTasks.length > 0 ? (count / filteredTasks.length) * 100 : 0
                    const CategoryIcon = getCategoryIcon(category.value)
                    return (
                      <div key={category.value} className="flex items-center justify-between p-3 bg-gov-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center justify-center w-8 h-8 bg-cyber-100 rounded-lg">
                            <CategoryIcon className="w-4 h-4 text-cyber-600" />
                          </div>
                          <span className="text-gov-700 font-medium">{category.label}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="w-24 bg-gov-200 rounded-full h-2">
                            <div
                              className="h-2 rounded-full bg-cyber-500 transition-all duration-500"
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                          <span className="text-gov-600 text-sm font-medium w-8 text-right">{count}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </motion.div>

              {/* Upcoming Deadlines */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="card"
              >
                <h3 className="text-lg font-semibold text-gov-800 mb-4">Upcoming Deadlines</h3>
                <div className="space-y-3">
                  {filteredTasks
                    .filter(task => task.dueDate && task.status !== 'completed')
                    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
                    .slice(0, 5)
                    .map((task) => {
                      const dueDate = new Date(task.dueDate)
                      const today = new Date()
                      const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24))
                      const isOverdue = daysUntilDue < 0
                      const isUrgent = daysUntilDue <= 3 && daysUntilDue >= 0
                      
                      return (
                        <div key={task.id} className="flex items-center justify-between p-3 bg-gov-50 rounded-lg hover:bg-gov-100 transition-colors cursor-pointer" onClick={() => setSelectedTask(task)}>
                          <div className="flex items-center space-x-3">
                            <div className={`w-3 h-3 rounded-full ${
                              isOverdue ? 'bg-red-500' : isUrgent ? 'bg-yellow-500' : 'bg-green-500'
                            }`}></div>
                            <div>
                              <p className="text-gov-800 font-medium truncate">{sanitizeDisplayTitle(task.title)}</p>
                              <p className="text-gov-600 text-sm">
                                {isOverdue ? `${Math.abs(daysUntilDue)} days overdue` : 
                                 daysUntilDue === 0 ? 'Due today' :
                                 daysUntilDue === 1 ? 'Due tomorrow' :
                                 `Due in ${daysUntilDue} days`}
                              </p>
                            </div>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full border ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </span>
                        </div>
                      )
                    })}
                  {filteredTasks.filter(task => task.dueDate && task.status !== 'completed').length === 0 && (
                    <div className="text-center py-8 text-gov-500">
                      <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No upcoming deadlines</p>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>

            {/* Performance Insights */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="card"
            >
              <h3 className="text-lg font-semibold text-gov-800 mb-4">Performance Insights</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-gov-50 rounded-lg">
                  <Award className="w-8 h-8 text-cyber-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gov-800">
                    {filteredTasks.length > 0 ? Math.round((filteredTasks.filter(t => t.status === 'completed').length / filteredTasks.length) * 100) : 0}%
                  </p>
                  <p className="text-gov-600 text-sm">Completion Rate</p>
                </div>
                <div className="text-center p-4 bg-gov-50 rounded-lg">
                  <Clock className="w-8 h-8 text-cyber-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gov-800">
                    {filteredTasks.reduce((sum, task) => sum + (task.estimatedHours || 0), 0)}h
                  </p>
                  <p className="text-gov-600 text-sm">Total Estimated Hours</p>
                </div>
                <div className="text-center p-4 bg-gov-50 rounded-lg">
                  <Target className="w-8 h-8 text-cyber-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gov-800">
                    {filteredTasks.filter(t => t.priority === 'high').length}
                  </p>
                  <p className="text-gov-600 text-sm">High Priority Tasks</p>
                </div>
              </div>
            </motion.div>

            {/* Recent Activity */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0 }}
              className="card"
            >
              <h3 className="text-lg font-semibold text-gov-800 mb-4">Recent Activity</h3>
              <div className="space-y-3">
                {recentActivities.length > 0 ? (
                  recentActivities.slice(0, 5).map((activity, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 bg-gov-50 rounded-lg">
                      <div className="w-2 h-2 bg-cyber-500 rounded-full mt-2 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gov-800 truncate">{activity.action}</p>
                        <p className="text-xs text-gov-500">{formatActivityTime(activity.timestamp)}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full border flex-shrink-0 ${
                        activity.type === 'create' ? 'bg-green-50 text-green-700 border-green-200' :
                        activity.type === 'update' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                        activity.type === 'delete' ? 'bg-red-50 text-red-700 border-red-200' :
                        activity.type === 'status_update' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                        'bg-gray-50 text-gray-700 border-gray-200'
                      }`}>
                        {activity.type === 'create' ? 'Created' :
                         activity.type === 'update' ? 'Updated' :
                         activity.type === 'delete' ? 'Deleted' :
                         activity.type === 'status_update' ? 'Status' :
                         'Activity'}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gov-500">
                    <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No recent activity</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </div>

      {/* Create Task Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gov-800">Create New Task</h2>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="p-2 text-gov-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Task Title *
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent ${
                          formErrors.title ? 'border-red-300' : 'border-gov-300'
                        }`}
                        placeholder="Enter task title"
                      />
                      {formErrors.title && (
                        <p className="text-red-600 text-sm mt-1">{formErrors.title}</p>
                      )}
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Description *
                      </label>
                      <textarea
                        rows={3}
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent ${
                          formErrors.description ? 'border-red-300' : 'border-gov-300'
                        }`}
                        placeholder="Describe the task and its requirements"
                      />
                      {formErrors.description && (
                        <p className="text-red-600 text-sm mt-1">{formErrors.description}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Category *
                      </label>
                      <select 
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent ${
                          formErrors.category ? 'border-red-300' : 'border-gov-300'
                        }`}
                      >
                        <option value="">Select category</option>
                        {categories.filter(cat => cat.value !== 'all').map(category => (
                          <option key={category.value} value={category.value}>{category.label}</option>
                        ))}
                      </select>
                      {formErrors.category && (
                        <p className="text-red-600 text-sm mt-1">{formErrors.category}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Priority *
                      </label>
                      <select
                        name="priority"
                        value={formData.priority}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent ${
                          formErrors.priority ? 'border-red-300' : 'border-gov-300'
                        }`}
                      >
                        <option value="">Select priority</option>
                        {priorities.filter(p => p.value !== 'all').map(priority => (
                          <option key={priority.value} value={priority.value}>
                            {priority.label}
                          </option>
                        ))}
                      </select>
                      {formErrors.priority && (
                        <p className="text-red-600 text-sm mt-1">{formErrors.priority}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Due Date *
                      </label>
                      <input
                        type="date"
                        name="due_date"
                        value={formData.due_date}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent ${
                          formErrors.due_date ? 'border-red-300' : 'border-gov-300'
                        }`}
                      />
                      {formErrors.due_date && (
                        <p className="text-red-600 text-sm mt-1">{formErrors.due_date}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Reminder Date *
                      </label>
                      <input
                        type="date"
                        className="w-full px-3 py-2 border border-gov-300 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Frequency
                      </label>
                      <select className="w-full px-3 py-2 border border-gov-300 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent">
                        <option value="once">One-time</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="semi-annually">Semi-annually</option>
                        <option value="annually">Annually</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Assigned To *
                      </label>
                      <select
                        name="assigned_to"
                        value={formData.assigned_to}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent ${
                          formErrors.assigned_to ? 'border-red-300' : 'border-gov-300'
                        }`}
                        disabled={loadingUsers}
                      >
                        <option value="">Select a user...</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name} ({user.email}) - {user.user_type?.replace('_', ' ')}
                          </option>
                        ))}
                      </select>
                      {loadingUsers && (
                        <p className="text-gray-500 text-sm mt-1">Loading users...</p>
                      )}
                      {formErrors.assigned_to && (
                        <p className="text-red-600 text-sm mt-1">{formErrors.assigned_to}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Estimated Hours
                      </label>
                      <input
                        type="number"
                        name="estimated_hours"
                        value={formData.estimated_hours}
                        onChange={handleInputChange}
                        min="1"
                        className="w-full px-3 py-2 border border-gov-300 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
                        placeholder="Hours needed to complete"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Compliance Type
                      </label>
                      <select className="w-full px-3 py-2 border border-gov-300 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent">
                        <option value="cybersecurity">Cybersecurity</option>
                        <option value="data-privacy">Data Privacy</option>
                        <option value="both">Both</option>
                      </select>
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Tags (comma-separated)
                      </label>
                      <input
                        type="text"
                        name="tags"
                        value={formData.tags}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gov-300 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
                        placeholder="training, mandatory, security"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Checklist Items
                      </label>
                      <div className="space-y-2">
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-gov-300 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
                          placeholder="Add checklist item"
                        />
                        <button
                          type="button"
                          className="text-cyber-600 hover:text-cyber-700 text-sm font-medium flex items-center space-x-1"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Add another item</span>
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-6 border-t border-gov-200">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      disabled={saving}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {saving && <Loader className="w-4 h-4 animate-spin" />}
                      <span>{saving ? 'Creating...' : 'Create Task'}</span>
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Task Modal */}
      <AnimatePresence>
        {showEditModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowEditModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gov-800">Edit Task</h2>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="p-2 text-gov-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Task Title *
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent ${
                          formErrors.title ? 'border-red-300' : 'border-gov-300'
                        }`}
                        placeholder="Enter task title"
                      />
                      {formErrors.title && (
                        <p className="text-red-600 text-sm mt-1">{formErrors.title}</p>
                      )}
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Description *
                      </label>
                      <textarea
                        rows={3}
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent ${
                          formErrors.description ? 'border-red-300' : 'border-gov-300'
                        }`}
                        placeholder="Describe the task and its requirements"
                      />
                      {formErrors.description && (
                        <p className="text-red-600 text-sm mt-1">{formErrors.description}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Category *
                      </label>
                      <select 
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent ${
                          formErrors.category ? 'border-red-300' : 'border-gov-300'
                        }`}
                      >
                        <option value="">Select category</option>
                        {categories.filter(cat => cat.value !== 'all').map(category => (
                          <option key={category.value} value={category.value}>{category.label}</option>
                        ))}
                      </select>
                      {formErrors.category && (
                        <p className="text-red-600 text-sm mt-1">{formErrors.category}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Priority *
                      </label>
                      <select
                        name="priority"
                        value={formData.priority}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent ${
                          formErrors.priority ? 'border-red-300' : 'border-gov-300'
                        }`}
                      >
                        <option value="">Select priority</option>
                        {priorities.filter(p => p.value !== 'all').map(priority => (
                          <option key={priority.value} value={priority.value}>
                            {priority.label}
                          </option>
                        ))}
                      </select>
                      {formErrors.priority && (
                        <p className="text-red-600 text-sm mt-1">{formErrors.priority}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Due Date *
                      </label>
                      <input
                        type="date"
                        name="due_date"
                        value={formData.due_date}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent ${
                          formErrors.due_date ? 'border-red-300' : 'border-gov-300'
                        }`}
                      />
                      {formErrors.due_date && (
                        <p className="text-red-600 text-sm mt-1">{formErrors.due_date}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Assigned To *
                      </label>
                      <select
                        name="assigned_to"
                        value={formData.assigned_to}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent ${
                          formErrors.assigned_to ? 'border-red-300' : 'border-gov-300'
                        }`}
                        disabled={loadingUsers}
                      >
                        <option value="">Select a user...</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name} ({user.email}) - {user.user_type?.replace('_', ' ')}
                          </option>
                        ))}
                      </select>
                      {loadingUsers && (
                        <p className="text-gray-500 text-sm mt-1">Loading users...</p>
                      )}
                      {formErrors.assigned_to && (
                        <p className="text-red-600 text-sm mt-1">{formErrors.assigned_to}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Estimated Hours
                      </label>
                      <input
                        type="number"
                        name="estimated_hours"
                        value={formData.estimated_hours}
                        onChange={handleInputChange}
                        min="1"
                        className="w-full px-3 py-2 border border-gov-300 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
                        placeholder="Hours needed to complete"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Tags
                      </label>
                      <input
                        type="text"
                        name="tags"
                        value={formData.tags}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gov-300 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
                        placeholder="training, mandatory, security"
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-6 border-t border-gov-200">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      disabled={saving}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {saving && <Loader className="w-4 h-4 animate-spin" />}
                      <span>{saving ? 'Updating...' : 'Update Task'}</span>
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Task Detail Modal */}
      <AnimatePresence>
        {selectedTask && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setSelectedTask(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gov-800">{selectedTask.title}</h2>
                  <button
                    onClick={() => setSelectedTask(null)}
                    className="p-2 text-gov-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gov-800 mb-2">Description</h3>
                    <p className="text-gov-600">{selectedTask.description}</p>
                  </div>
                  
                  {/* Admin Risk Details */}
                  {selectedTask.isAdminRisk && selectedTask.riskDetails && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-blue-800 mb-3 flex items-center">
                        <Shield className="w-5 h-5 mr-2" />
                        Admin-Assigned Risk Details
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-medium text-blue-800 mb-1">Risk Category</h4>
                          <p className="text-blue-700">{selectedTask.riskDetails.riskCategory}</p>
                        </div>
                        <div>
                          <h4 className="font-medium text-blue-800 mb-1">Severity</h4>
                          <span className={`px-2 py-1 text-xs rounded-full border ${
                            selectedTask.riskDetails.severity === 'critical' ? 'bg-red-100 text-red-800 border-red-200' :
                            selectedTask.riskDetails.severity === 'high' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                            selectedTask.riskDetails.severity === 'medium' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                            'bg-green-100 text-green-800 border-green-200'
                          }`}>
                            {selectedTask.riskDetails.severity}
                          </span>
                        </div>
                        <div className="col-span-2">
                          <h4 className="font-medium text-blue-800 mb-1">Impact Description</h4>
                          <p className="text-blue-700">{selectedTask.riskDetails.impact}</p>
                        </div>
                        <div>
                          <h4 className="font-medium text-blue-800 mb-1">Likelihood</h4>
                          <p className="text-blue-700 capitalize">{selectedTask.riskDetails.likelihood}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-gov-800 mb-1">Priority</h4>
                      <span className={`px-2 py-1 text-xs rounded-full border ${getPriorityColor(selectedTask.priority)}`}>
                        {selectedTask.priority}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-medium text-gov-800 mb-1">Status</h4>
                      <span className={`px-2 py-1 text-xs rounded-full border ${getStatusColor(selectedTask.status)}`}>
                        {selectedTask.status}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-medium text-gov-800 mb-1">Due Date</h4>
                      <p className="text-gov-600">{new Date(selectedTask.dueDate).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gov-800 mb-1">Assigned To</h4>
                      <p className="text-gov-600">{selectedTask.assignedTo}</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gov-800 mb-1">Frequency</h4>
                      <p className="text-gov-600">{selectedTask.frequency}</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gov-800 mb-1">Estimated Hours</h4>
                      <p className="text-gov-600">{selectedTask.estimatedHours || selectedTask.estimated_hours || 'Not specified'}h</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gov-800 mb-1">Compliance Type</h4>
                      <p className="text-gov-600">{selectedTask.complianceType}</p>
                    </div>
                  </div>
                  
                  {selectedTask.completionRate > 0 && (
                    <div>
                      <h4 className="font-medium text-gov-800 mb-2">Progress</h4>
                      <div className="flex items-center justify-between text-sm text-gov-600 mb-1">
                        <span>Completion</span>
                        <span>{selectedTask.completionRate}%</span>
                      </div>
                      <div className="w-full bg-gov-200 rounded-full h-2">
                        <div 
                          className="bg-cyber-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${selectedTask.completionRate}%` }}
                        />
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gov-800 mb-3">Checklist</h3>
                    <div className="space-y-2">
                      {selectedTask.checklist.map((item, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-cyber-600 border-gov-300 rounded focus:ring-cyber-500"
                          />
                          <span className="text-gov-700">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex justify-end space-x-3">
                    {selectedTask.isAdminRisk ? (
                      <button 
                        onClick={() => handleAcknowledgeRisk(selectedTask)}
                        className="btn-primary"
                      >
                        Acknowledge Risk
                      </button>
                    ) : (
                      <>
                        <button 
                          onClick={() => handleEditTask(selectedTask)}
                          className="btn-secondary"
                        >
                          Edit Task
                        </button>
                        <button className="btn-primary">Mark Complete</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default TaskReminders
